import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Player, TeamStatsDoc, HonorScore, SeasonRecapDoc } from '@/types';
import { Trophy, TrendingUp, Target, ChevronRight, Award, ShieldCheck, Sun } from 'lucide-react';
import { SEASON, currentSeasonLine } from '@/config/season';

const Board: React.FC<{
  icon: React.ReactNode;
  title: string;
  sub: string;
  rows: { playerId: string; name: string; value: React.ReactNode; detail?: string }[];
}> = ({ icon, title, sub, rows }) => (
  <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 overflow-hidden">
    <div className="px-5 py-4 border-b border-willard-grey-800">
      <p className="font-heading text-2xl tracking-wide uppercase flex items-center gap-2">
        <span className="text-willard-taupe">{icon}</span> {title}
      </p>
      <p className="text-xs text-willard-grey-500 mt-0.5">{sub}</p>
    </div>
    <ol>
      {rows.map((r, i) => (
        <li key={r.playerId + i} className="border-b border-willard-grey-800 last:border-0">
          <Link to={`/player/${r.playerId}`} className="flex items-center gap-3 px-5 py-3 hover:bg-willard-grey-800/60 transition-colors group">
            <span className={`font-display text-lg w-6 ${i === 0 ? 'text-willard-taupe' : 'text-willard-grey-600'}`}>{i + 1}</span>
            <span className="font-bold group-hover:text-willard-taupe transition-colors">{r.name}</span>
            <span className="ml-auto font-display text-2xl">{r.value}</span>
            {r.detail && <span className="text-xs text-willard-grey-500 w-20 text-right">{r.detail}</span>}
          </Link>
        </li>
      ))}
    </ol>
  </div>
);

export const Stats: React.FC = () => {
  const [team, setTeam] = useState<TeamStatsDoc | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [honors, setHonors] = useState<HonorScore[]>([]);
  const [recap, setRecap] = useState<SeasonRecapDoc | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'teamStats', 'current')).catch(() => null),
      getDocs(query(collection(db, 'players'), where('published', '==', true), where('consentOnFile', '==', true))).catch(() => null),
      // Public honor scores only (rules enforce public==true for anonymous reads)
      getDocs(query(collection(db, 'honorScores'), where('public', '==', true))).catch(() => null),
      // Last season frozen by sync-stats.mjs (public read)
      getDoc(doc(db, 'teamStats', `recap-${SEASON.recap.key}`)).catch(() => null),
    ])
      .then(([t, p, h, r]) => {
        if (t?.exists()) setTeam(t.data() as TeamStatsDoc);
        if (r?.exists()) setRecap(r.data() as SeasonRecapDoc);
        if (p) {
          setPlayers(
            p.docs
              .map((d) => ({ id: d.id, ...d.data() }) as Player)
              .filter((pl) => pl.willardTeam !== false && pl.isActive !== false && pl.removed !== true)
          );
        }
        if (h) {
          setHonors(
            h.docs
              .map((d) => ({ id: d.id, ...d.data() }) as HonorScore)
              .sort((a, b) => (b.score || 0) - (a.score || 0))
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Current-season rows straight from live LS data on published players.
  // Baseline is the official book average, never the Stats-app blend.
  // Two boards, two clean questions (DC 2026-09-09): who has the highest average,
  // and who has gained the most vs last season. Never blended into one rank.
  const seasonRows = players
    .map((p) => {
      const l = currentSeasonLine(p);
      const book = p.lastSeasonBookAverage || 0;
      return l ? { p, l, delta: book > 0 ? Math.round(l.currentAverage - book) : null } : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
  const byAverage = [...seasonRows].sort((a, b) => b.l.currentAverage - a.l.currentAverage || b.l.games - a.l.games);
  const byGain = seasonRows
    .filter((r) => r.delta !== null && r.delta > 0)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0) || b.l.currentAverage - a.l.currentAverage);
  const gamesIn = Math.max(0, ...seasonRows.map((r) => r.l.games));

  if (loading) {
    return (
      <div className="bg-willard-black min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-willard-taupe" />
      </div>
    );
  }

  return (
    <div className="bg-willard-black text-white min-h-screen">
      <div className="max-w-6xl mx-auto px-5 py-14">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-willard-taupe mb-2">{SEASON.current.label} · Willard Tigers</p>
        <h1 className="font-display text-5xl sm:text-6xl mb-3">TEAM STATS</h1>
        <p className="text-willard-grey-400 max-w-2xl mb-10">
          All numbers come from sanctioned league play: USBC book averages and LeagueSecretary league data,
          updated nightly. Tap any bowler for their full profile.
        </p>

        {team && team.totalGames > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-willard-grey-800 border border-willard-grey-800 rounded-xl overflow-hidden mb-12">
            {[
              [team.teamAverage, 'Team Average'],
              [team.activePlayers, 'High School Bowlers'],
              [team.totalGames.toLocaleString(), 'Games Tracked'],
              [team.highGame, 'League High Game'],
            ].map(([v, l]) => (
              <div key={String(l)} className="bg-willard-black text-center px-4 py-6">
                <p className="font-display text-4xl">{v}</p>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mt-2">{l}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {byAverage.length > 0 && (
            <Board
              icon={<Target className="w-5 h-5" />}
              title="Fall Averages"
              sub={`${SEASON.current.label} league average, ${gamesIn} games in`}
              rows={byAverage.slice(0, 10).map(({ p, l }) => ({
                playerId: p.id,
                name: p.name,
                value: l.currentAverage,
                detail: `HG ${l.highGame}`,
              }))}
            />
          )}
          {byGain.length > 0 && (
            <Board
              icon={<TrendingUp className="w-5 h-5" />}
              title="Most Improved"
              sub={`Gain vs last season's book average, ${gamesIn} games in`}
              rows={byGain.slice(0, 10).map(({ p, l, delta }) => ({
                playerId: p.id,
                name: p.name,
                value: <span className="text-green-400">+{delta}</span>,
                detail: `${l.currentAverage} avg`,
              }))}
            />
          )}
          {recap && recap.lines.length > 0 && (
            <Board
              icon={<Sun className="w-5 h-5" />}
              title={`${recap.label} Recap`}
              sub="Final summer league averages"
              rows={[...recap.lines]
                .sort((a, b) => b.average - a.average || b.games - a.games)
                .slice(0, 10)
                .map((l) => ({
                  playerId: l.playerId,
                  name: l.name,
                  value: l.average,
                  detail: l.delta !== null && l.delta > 0 ? `+${l.delta}` : `${l.games} games`,
                }))}
            />
          )}
          {(team?.leadersByAverage || []).length > 0 && (
            <Board
              icon={<Target className="w-5 h-5" />}
              title="Book Average"
              sub="Official USBC book average, latest season"
              rows={(team!.leadersByAverage || []).map((r) => ({ playerId: r.playerId, name: r.name, value: r.value }))}
            />
          )}
          {(team?.leadersByHighGame || []).length > 0 && (
            <Board
              icon={<Trophy className="w-5 h-5" />}
              title="High Game"
              sub="Best single game in league play"
              rows={(team!.leadersByHighGame || []).map((r) => ({ playerId: r.playerId, name: r.name, value: r.value }))}
            />
          )}
        </div>

        {/* 300 Club / Honor Roll */}
        {honors.length > 0 && (
          <section className="mt-14">
            <div className="flex items-center gap-3 mb-1">
              <Award className="w-7 h-7 text-willard-taupe" />
              <h2 className="font-heading text-3xl tracking-wide uppercase">300 Club &amp; Honor Roll</h2>
            </div>
            <p className="text-xs text-willard-grey-500 mb-6">
              Perfect games, honor series, and milestones, verified by the coaching staff.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {honors.map((h) => {
                const isPerfect = h.score === 300;
                return (
                  <div
                    key={h.id}
                    className={`rounded-xl border p-5 flex items-center gap-4 ${
                      isPerfect
                        ? 'bg-gradient-to-br from-willard-taupe/20 to-willard-grey-900 border-willard-taupe/60'
                        : 'bg-willard-grey-900 border-willard-grey-800'
                    }`}
                  >
                    <div
                      className={`shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-display text-2xl ${
                        isPerfect ? 'bg-willard-taupe text-willard-black' : 'bg-willard-grey-800 text-willard-taupe'
                      }`}
                    >
                      {h.score || <Award className="w-7 h-7" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-lg truncate">{h.bowlerName}</p>
                      <p className="text-xs text-willard-grey-400">
                        {[h.label, h.league, h.date].filter(Boolean).join(' · ') ||
                          (h.type === '800' || h.type === '700' ? `${h.type} series` : `${h.type} game`)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-willard-grey-500">
                          {h.sanctioning || 'USBC'}
                        </span>
                        {h.verified && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-widest text-willard-taupe">
                            <ShieldCheck className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="mt-12 border border-willard-grey-800 rounded-xl px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-willard-grey-400 text-sm max-w-xl">
            Want the deep numbers? Every Tiger's game is tracked frame by frame (strike rates, spare conversion,
            splits, streaks) in our Stats Tracker.
          </p>
          <a
            href="https://bowlingstatstracker.web.app"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-1 text-willard-taupe font-bold text-sm hover:text-white transition-colors"
          >
            Open Stats Tracker <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};
